import { ChangeDetectorRef, Component, inject, OnInit, signal, ViewChild } from "@angular/core";
import { Router } from "@angular/router";
import { Subject, Observable, Subscription } from "rxjs";
import { ActivatedRoute, Params } from "@angular/router";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { MatDatepicker, MatCalendarView } from "@angular/material/datepicker";
import { UntypedFormControl } from "@angular/forms";
import { DateTime } from "luxon";
import { environment } from "../../../environments/environment";
import { AnalyticsService } from "../../service/analytics.service";
import { GeneralService } from "../../service/general.service";
import { Pair } from "../../obj/Pair";
import { IIconDialog } from "../incs/i-dialog.component";
import { EntityBase } from "../../entity/EntityBase";
import { Account } from "../../entity/Account";
import { InfoDialog } from "../incs/info-dialog.component";
import { ErrorImmutable } from "../../obj/ErrorImmutable";

@Component({
    template: "",
    standalone: false
})
export abstract class AbstractComponent implements OnInit {

    cdr = inject(ChangeDetectorRef);

    errorY = signal(new ErrorImmutable("", []));
    RECS_PER_PAGE: number = environment.RECS_PER_PAGE;

    eventSubscriptions: Subscription[] = [];

    focusedField = signal<string>('');
    iIconDialogRef: MatDialogRef<IIconDialog> = null!;
    private configLoaded = new Subject<boolean>();
    configLoaded$ = this.configLoaded.asObservable();

    config: Pair[] = [];
    currentDate: DateTime;

    previousUrl: string = "";
    currentUrl: string = "";

    pageLoaded = signal<boolean>(false);
    initPromises: Promise<any>[] = [];
    infoDialogRef: MatDialogRef<InfoDialog> | null = null;

    constructor(
        protected router: Router,
        protected route: ActivatedRoute,
        protected dialog: MatDialog,
        protected analyticsService: AnalyticsService,
        protected generalService: GeneralService
    ) {
        this.initPromises.push(
            this.generalService.getSiteConfig()
                .then(conf => {
                    this.config = conf;
                    this.configLoaded.next(true);
                })
        );
        this.initPromises.push(this.refreshTime());
        let newUrl = this.router.url;
        let prevUrl = <string>this.generalService.getFromSession("previousUrl");
        let curUrl = <string>this.generalService.getFromSession("currentUrl");
        if (this.route.snapshot.routeConfig != null) {
            if (!this._compareUrl(curUrl, newUrl)) {
                if (curUrl != null) {
                    this.generalService.storeInSession("previousUrl", curUrl);
                    this.previousUrl = curUrl;
                } else {
                    this.generalService.storeInSession("previousUrl", "/");
                    this.previousUrl = "/";
                }
                this.generalService.storeInSession("currentUrl", newUrl);
                this.currentUrl = newUrl;
            } else {
                this.previousUrl = prevUrl;
                this.currentUrl = curUrl;
            }
        }
    }

    ngOnInit(): void {
        if (this.initPromises.length == 0) {
            this.pageLoaded.set(true);
            this.pageStart();
            return;
        }
        Promise.all(this.initPromises).then(data => {
            this.pageLoaded.set(true);
            this.pageStart();
        }
        );
    }

    pageStart(): void {

    }

    ngOnDestroy() {
        for (let i = this.eventSubscriptions.length - 1; i >= 0; i--) {
            if (this.eventSubscriptions[i] != null) {
                this.eventSubscriptions[i].unsubscribe();
            }
            this.eventSubscriptions.pop();
        }
    }

    refreshTime(): Promise<boolean> {
        return this.generalService.now().then(now => {
            this.currentDate = DateTime.fromMillis(now * 1000, { zone: "America/New_York" });
            this.currentDate = this.currentDate.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
            return true;
        });
    }

    refreshView(): void {
        this.cdr.markForCheck();
    }

    applySitesTimeZone(d: DateTime): DateTime {
        return this.currentDate.set({ day: d.day, month: d.month, year: d.year, hour: d.hour, minute: d.minute, second: d.second, millisecond: d.millisecond });
    }

    getClassesForInput(field: string, isRequired: boolean, value: string | null): string {
        var res = "";
        if (isRequired) {
            if (res.length > 0) {
                res += " ";
            }
            res += "required";
        }
        if (this.errorY().isWrong(field)) {
            if (res.length > 0) {
                res += " ";
            }
            res += "error";
        }
        if (value != null && value.length > 0) {
            if (res.length > 0) {
                res += " ";
            }
            res += "placeholder";
        }
        return res;
    }

    getVisibilityForLabel(value: string | null): boolean {
        if (value != null) {
            value.trim();
            if (value.length > 0) {
                return true;
            }
        }
        return false;
    }

    focusField(field: string): void {
        this.focusedField.set(field);
    }

    focusOutField(field: string): void {
        if (this.focusedField() == field) {
            this.focusedField.set("");
        }
    }

    clearErrors(): void {
        this.errorY.set(this.errorY().clear());
    }
    addError(field: string): void {
        this.errorY.set(this.errorY().addToErrors(field));
    }
    resetError(field: string): void {
        this.errorY.set(this.errorY().removeFromErrors(field));
    }
    setErrorText(error: string): void {
        this.errorY.set(this.errorY().setError(error));
    }
    compareFn(c1: EntityBase | null, c2: EntityBase | null): boolean {
        if (c1 == null && c2 == null) {
            return true;
        }
        return c1 && c2 ? c1.id === c2.id : c1 === c2;
    }

    compareKeyFn(c1: Pair, c2: Pair): boolean {
        return c1 && c2 ? c1.key === c2.key : c1 === c2;
    }

    iIconPopup(iinfo: string, classname: string = "i-icon-popup"): void {
        this.iIconDialogRef = this.dialog.open(IIconDialog, {
            disableClose: false,
            panelClass: classname
        });
        this.iIconDialogRef.componentInstance.infoMessage = iinfo;
        this.iIconDialogRef.afterClosed().subscribe(result => {
            this.iIconDialogRef = null!;
        });
    }

    iIconPopupElement(element: any, classname: string = "i-icon-popup"): void {
        this.iIconPopup(element.innerHTML, classname);
    }

    openResultsPopup(title: string, info: string, afterClose: any): void {
        this.infoDialogRef = this.dialog.open(InfoDialog, {
            disableClose: false
            , panelClass: "i-icon-popup"
        });
        this.infoDialogRef.componentInstance.infoTitle = title;
        this.infoDialogRef.componentInstance.infoMessage = info;
        if (afterClose != null) {
            this.infoDialogRef.afterClosed().subscribe(result => {
                this.infoDialogRef = null;
                afterClose();
            });
        }
    }

    chosenYearHandler(normalizedYear: DateTime, field: UntypedFormControl) {
        var ctrlValue = field.value;
        if (ctrlValue == null) {
            ctrlValue = this.currentDate;
        }
        ctrlValue = ctrlValue.set({ year: normalizedYear.year });
        field.setValue(ctrlValue);
    }

    chosenMonthHandler(normalizedMonth: DateTime, field: UntypedFormControl, fieldName: string, datepicker: MatDatepicker<any>) {
        var ctrlValue = field.value;
        if (ctrlValue == null) {
            ctrlValue = this.currentDate;
        }
        ctrlValue = ctrlValue.set({ year: normalizedMonth.year, month: normalizedMonth.month, day: 1, hour: 0, minute: 0, second: 0, millisecond: 0 });

        field.setValue(ctrlValue);
        this.resetError(fieldName);
        datepicker.close();
    }

    accountIdent(account: Account): string {
        return Account.accountIdent(account);
    }

    positive(val: number | null): number {
        if (val == null) {
            return 0;
        }
        return Math.abs(val);
    }

    compareDates(val1: Date | null, val2: Date | null): boolean {
        if (val1 == null || val2 == null) {
            return true;
        }
        return val1 > val2;
    }

    getTermPeriod(termFrom: Date | null, termTo: Date | null): number {
        if (termFrom == null || termTo == null) {
            return 0;
        }
        var termFromDT = DateTime.fromJSDate(termFrom);
        var termToDT = DateTime.fromJSDate(termTo);
        var diff = termToDT.diff(termFromDT, ["months"]);
        return Math.floor(diff["months"]);
    }

    getTermTo(termFrom: Date, months: number): Date {
        return DateTime.fromJSDate(termFrom).plus({ months: months }).toJSDate();
    }

    getDateFromRatesString(inStr: string, useEndOfPeriod: boolean): DateTime {
        let regexpQuartal = /Q(\d)\/(\d+)/g;
        let regexpYear = /^(\d+)$/g;
        if (!inStr.match(regexpQuartal) && !inStr.match(regexpYear)) {
            let d = DateTime.fromFormat(inStr, "M/yyyy");
            if (useEndOfPeriod) {
                d = d.endOf('month');
            }
            return d;
        }
        if (inStr.match(regexpYear)) {
            let d = DateTime.fromFormat(inStr, "yyyy");
            if (useEndOfPeriod) {
                d = d.endOf('month');
            }
            return d;
        }
        let q = inStr.substring(1, 2);
        let y = inStr.substring(3);
        let d = DateTime.fromObject({ year: parseInt(y), month: (parseInt(q) - 1) * 3 + 1, day: 1, hour: 0, minute: 0 });
        if (useEndOfPeriod) {
            d = d.plus({ month: 3 }).endOf('month');
        }
        return d;
    }

    estimateAvg(inArr: number[]): number {
        let inSum = 0;
        if (inArr.length == 0) {
            return 0;
        }
        for (let i = 0; i < inArr.length; i++) {
            inSum += inArr[i];
        }
        return inSum / inArr.length;
    }

    getKilowantColor(): string {
        return "#" + environment.KILOWANT_COLOR;
    }

    goto(url: string | null): void {
        if (url != null && url != '') {
            this.generalService.closeSidenav();
            this.router.navigate([url]);
        }
    }

    private _compareUrl(url1: string | null, url2: string | null): boolean {
        if (url1 == null || url2 == null) {
            return false;
        }
        return (url1.split("#").shift() == url2.split("#").shift());
    }

}